import { ReactElement } from "react";
import Head from "next/head";
import Image from "next/image";
import { NextSeo } from "next-seo";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { LayoutEmpty } from "components/layouts/LayoutEmpty";
import { ClaimsGuideContent } from "components/organisms/ClaimsGuideContent";

// The uncollapsed wordmarks are named for their ink colour: the "dark-mode"
// wordmark is dark ink, so it belongs to the light theme (same as AppSidebar).
import WordmarkLightTheme from "../public/images/dark-mode-icon-uncollapsed.svg";
import WordmarkDarkTheme from "../public/images/light-mode-icon-uncollapsed.svg";

export default function ClaimsGuidePage(): JSX.Element {
  const { theme: themeName } = useSelector(
    (state: RootState) => state.tableSlice,
  );
  const wordmarkLogo =
    themeName === "dark" ? WordmarkDarkTheme : WordmarkLightTheme;
  return (
    <>
      <NextSeo
        title="How to Make a Claim on Connexx"
        description="How to submit a claim in Connexx for a parcel that is lost or arrives damaged."
      />
      <Head>
        <meta name="robots" content="index, follow" />
      </Head>

      <div
        className="min-h-screen"
        style={{
          backgroundColor: "var(--bodyBackgroundColor, var(--backgroundColor))",
          color: "var(--textColorPrimary)",
        }}
      >
        <header
          className="border-b"
          style={{ borderColor: "var(--borderColor)" }}
        >
          <div className="w-full px-6 py-5 flex items-center justify-between">
            {/* 124px matches the wordmark's rendered width in the expanded
                sidebar (198px sidebar minus padding and the collapse button) */}
            <Image
              src={wordmarkLogo}
              alt="Connexx"
              className="h-auto"
              style={{ width: 124 }}
            />
            <a
              href="https://connexx.co.uk/"
              className="inline-flex items-center justify-center px-5 py-2.5 rounded-md text-sm font-semibold"
              style={{
                backgroundColor: "var(--colorsPrimary11)",
                color: "#fff",
              }}
            >
              Log in
            </a>
          </div>
        </header>

        <main className="mx-auto max-w-3xl px-6 py-12 lg:py-16">
          <h1 className="text-3xl sm:text-4xl font-bold mb-8">
            How to Make a Claim on Connexx
          </h1>
          <ClaimsGuideContent />
        </main>
      </div>
    </>
  );
}

ClaimsGuidePage.isPublic = true;
ClaimsGuidePage.getLayout = (page: ReactElement) => (
  <LayoutEmpty>{page}</LayoutEmpty>
);
